import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

const Terms = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="rounded-none hover:bg-slate-50 px-2"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="font-heading text-2xl font-bold text-slate-900">PayForeman</h1>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/login')}
                className="rounded-none border-slate-300 hover:bg-slate-50"
              >
                Log In
              </Button>
              <Button
                onClick={() => navigate('/register')}
                className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
              >
                Register
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="font-heading text-4xl font-bold text-slate-900 mb-2">Terms of Service</h1>
          <p className="text-sm text-slate-600">Last Updated: January 1, 2025</p>
        </div>

        <div className="prose prose-slate max-w-none space-y-8">
          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              These Terms of Service ("Terms") constitute a legally binding agreement between you ("User," "you,"
              or "your") and Ardito Advisors LLC ("Company," "we," "our," or "us") concerning your access to and
              use of the PayForeman service at https://payforeman.arditoadvisors.com (the "Service").
            </p>
            <p className="text-slate-700 leading-relaxed">
              By accessing or using the Service, you agree to be bound by these Terms and our Privacy Policy.
              If you do not agree to these Terms, you must not access or use the Service.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">2. Description of Service</h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              PayForeman is a cloud-based collections management platform that provides:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>Invoice tracking and management</li>
              <li>Collections workflow automation</li>
              <li>Accounts receivable aging reports</li>
              <li>Customer and payment data management</li>
              <li>Analytics and reporting tools</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time
              with or without notice.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">3. User Accounts</h2>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-4">3.1 Account Creation</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              To use the Service, you must create an account by providing accurate and complete information.
              You are responsible for maintaining the confidentiality of your account credentials and for all
              activities that occur under your account.
            </p>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">3.2 Account Responsibilities</h3>
            <p className="text-slate-700 leading-relaxed mb-3">You agree to:</p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security of your password</li>
              <li>Notify us immediately of any unauthorized use</li>
              <li>Accept responsibility for all activities under your account</li>
            </ul>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-4">3.3 Account Termination</h3>
            <p className="text-slate-700 leading-relaxed">
              We may suspend or terminate your account if you violate these Terms or engage in fraudulent,
              abusive, or illegal activity.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">4. Acceptable Use</h2>
            <p className="text-slate-700 leading-relaxed mb-3">You agree NOT to:</p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>Use the Service for any illegal purpose or in violation of any laws</li>
              <li>Violate or infringe the rights of others, including intellectual property rights</li>
              <li>Upload or transmit viruses, malware, or other malicious code</li>
              <li>Attempt to gain unauthorized access to the Service or related systems</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Impersonate any person or entity</li>
              <li>Collect or harvest information about other users</li>
              <li>Use the Service to send spam or unsolicited communications</li>
              <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
              <li>Remove or alter any proprietary notices</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">5. User Data and Content</h2>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">5.1 Your Data</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              You retain all rights to the data you upload to the Service ("User Data"). By uploading User Data,
              you grant us a limited license to store, process, and display your data solely for the purpose of
              providing the Service to you.
            </p>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">5.2 Data Accuracy</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              You are solely responsible for the accuracy, quality, and legality of your User Data. You represent
              and warrant that you have all necessary rights to upload and use the User Data through the Service.
            </p>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">5.3 Data Backup</h3>
            <p className="text-slate-700 leading-relaxed">
              While we implement backup procedures, you are responsible for maintaining your own backups of User Data.
              We are not liable for any loss or corruption of User Data.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">6. Fees and Payment</h2>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">6.1 Subscription Fees</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Certain features of the Service may require payment of subscription fees. All fees are non-refundable
              unless otherwise stated. We reserve the right to change our fees at any time with reasonable notice.
            </p>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">6.2 Payment Terms</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If you subscribe to a paid plan, you agree to pay all applicable fees on the billing schedule
              specified for your plan. Fees will be billed in advance and are due upon receipt.
            </p>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">6.3 Late Payment</h3>
            <p className="text-slate-700 leading-relaxed">
              Failure to pay fees when due may result in suspension or termination of your account. You remain
              responsible for all fees incurred during the subscription period.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">7. Intellectual Property</h2>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">7.1 Our Property</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The Service, including all content, features, functionality, software, and design, is owned by
              Ardito Advisors LLC and is protected by United States and international copyright, trademark,
              patent, trade secret, and other intellectual property laws.
            </p>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">7.2 Limited License</h3>
            <p className="text-slate-700 leading-relaxed">
              We grant you a limited, non-exclusive, non-transferable, revocable license to access and use the
              Service for your internal business purposes in accordance with these Terms.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">8. Disclaimers and Limitations of Liability</h2>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">8.1 Service "As Is"</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS
              OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
              PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">8.2 No Guarantee</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              We do not warrant that the Service will be uninterrupted, error-free, secure, or free from viruses
              or other harmful components. We make no guarantees regarding the accuracy, reliability, or completeness
              of any content or data provided through the Service.
            </p>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">8.3 Limitation of Liability</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, ARDITO ADVISORS LLC SHALL NOT BE LIABLE FOR ANY INDIRECT,
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER
              INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES,
              RESULTING FROM:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>Your access to or use of (or inability to access or use) the Service</li>
              <li>Any conduct or content of any third party on the Service</li>
              <li>Any content obtained from the Service</li>
              <li>Unauthorized access, use, or alteration of your data or content</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              IN NO EVENT SHALL OUR TOTAL LIABILITY EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS
              PRIOR TO THE CLAIM, OR $100, WHICHEVER IS GREATER.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">9. Indemnification</h2>
            <p className="text-slate-700 leading-relaxed">
              You agree to indemnify, defend, and hold harmless Ardito Advisors LLC and its officers, directors,
              employees, and agents from any claims, liabilities, damages, losses, and expenses, including reasonable
              attorneys' fees, arising out of or in any way connected with your access to or use of the Service,
              your violation of these Terms, or your violation of any rights of another party.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">10. Dispute Resolution</h2>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">10.1 Informal Resolution</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If you have any dispute with us, you agree to first contact us at support@arditoadvisors.com and
              attempt to resolve the dispute informally.
            </p>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">10.2 Arbitration</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Any dispute that cannot be resolved informally shall be resolved by binding arbitration in accordance
              with the rules of the American Arbitration Association. The arbitration will be conducted in the
              United States, and judgment on the arbitration award may be entered in any court with jurisdiction.
            </p>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">10.3 Class Action Waiver</h3>
            <p className="text-slate-700 leading-relaxed">
              You agree that any arbitration or proceeding shall be limited to the dispute between you and us
              individually. You waive any right to participate in a class action lawsuit or class-wide arbitration.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">11. Governing Law</h2>
            <p className="text-slate-700 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the United States
              and the state where Ardito Advisors LLC is registered, without regard to its conflict of law provisions.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">12. Changes to Terms</h2>
            <p className="text-slate-700 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will notify you of material changes by
              posting the updated Terms on this page and updating the "Last Updated" date. Your continued use of
              the Service after such changes constitutes your acceptance of the new Terms.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">13. Termination</h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              Either party may terminate this agreement at any time:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>You may cancel your account at any time through the Service settings or by contacting support</li>
              <li>We may suspend or terminate your access immediately for any violation of these Terms</li>
              <li>Upon termination, your right to use the Service will immediately cease</li>
              <li>We may delete your User Data after account termination (subject to legal retention requirements)</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">14. General Provisions</h2>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">14.1 Entire Agreement</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and
              Ardito Advisors LLC regarding the Service.
            </p>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">14.2 Severability</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions
              will continue in full force and effect.
            </p>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">14.3 Waiver</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Our failure to enforce any right or provision of these Terms will not be deemed a waiver of such
              right or provision.
            </p>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">14.4 Assignment</h3>
            <p className="text-slate-700 leading-relaxed">
              You may not assign or transfer these Terms without our prior written consent. We may assign these
              Terms without restriction.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">15. Contact Information</h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              If you have questions about these Terms, please contact us:
            </p>
            <div className="bg-slate-50 p-4 border border-slate-200">
              <p className="text-slate-900 font-semibold mb-2">Ardito Advisors LLC</p>
              <p className="text-slate-700">Email: support@arditoadvisors.com</p>
              <p className="text-slate-700">Website: https://payforeman.arditoadvisors.com</p>
            </div>
          </section>
        </div>

        {/* Back to home */}
        <div className="mt-12 pt-8 border-t border-slate-200">
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="rounded-none border-slate-300 hover:bg-slate-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-slate-50 border-t border-slate-200 mt-16">
        <div className="max-w-7xl mx-auto text-center text-sm text-slate-600">
          <p>© 2025 Ardito Advisors LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
